import { useRef, type KeyboardEvent, type ReactNode } from 'react';

/**
 * The page head of /strategic-kpis (masterplan skpi-redesign §3 E3, mockup «Page head»; package A2a):
 * the title, an optional subtitle, and the tabs as an ARIA segment — a real tablist drawn with the kit's
 * `.seg.lg`, roving tab stop, ←/→/Home/End move and select (S2-07 of the K5 audit, kept). A tab may carry
 * a count (Data quality, E3) in the amber `.cnt`; the count is part of the tab's name.
 *
 * No data logic: the page owns the active tab and the tab panel; `tabId` and `SKPI_TABPANEL_ID` are the
 * ids the panel's `aria-labelledby` and `id` use.
 */

export const SKPI_TABPANEL_ID = 'skpi-tabpanel';
export const skpiTabId = (key: string) => `skpi-tab-${key}`;

export interface PageHeadTab<K extends string> {
    key: K;
    label: string;
    /** A count beside the label; 0 or undefined shows none. */
    count?: number;
    /** What the count counts, for the tab's accessible name («2 to check»). */
    countLabel?: string;
}

interface Props<K extends string> {
    title: string;
    sub?: ReactNode;
    tabs: readonly PageHeadTab<K>[];
    active: K;
    onSelect: (key: K) => void;
    /** Name of the tablist. */
    ariaLabel?: string;
    /** Above the title row: the crumbs of the drill view, for instance. */
    top?: ReactNode;
    /** Right end of the head (a button or two). */
    actions?: ReactNode;
}

export function PageHead<K extends string>({ title, sub, tabs, active, onSelect, ariaLabel = 'Page sections', top, actions }: Props<K>) {
    const refs = useRef<Partial<Record<K, HTMLButtonElement | null>>>({});

    const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
        e.preventDefault();
        const n = tabs.length;
        const i = Math.max(0, tabs.findIndex(t => t.key === active));
        const j = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowRight' ? 1 : n - 1)) % n;
        onSelect(tabs[j].key);
        refs.current[tabs[j].key]?.focus();
    };

    return (
        <header className={`page-head${top ? ' has-top' : ''}`}>
            {top && <div className="page-top">{top}</div>}
            <div className="titles">
                <h1>{title}</h1>
                {sub && <p className="sub">{sub}</p>}
            </div>
            <div className="seg lg" role="tablist" aria-label={ariaLabel} onKeyDown={onKey}>
                {tabs.map(t => {
                    const selected = t.key === active;
                    const count = t.count ?? 0;
                    return (
                        <button
                            key={t.key}
                            ref={el => { refs.current[t.key] = el; }}
                            id={skpiTabId(t.key)}
                            type="button"
                            role="tab"
                            aria-selected={selected}
                            aria-controls={SKPI_TABPANEL_ID}
                            tabIndex={selected ? 0 : -1}
                            onClick={() => onSelect(t.key)}
                            data-testid={`skpi-tab-${t.key}`}
                        >
                            {t.label}
                            {count > 0 && (
                                <span className="cnt">
                                    <span className="sr-only">, </span>{count}
                                    {t.countLabel && <span className="sr-only"> {t.countLabel}</span>}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
            {actions && <div className="page-actions">{actions}</div>}
        </header>
    );
}
